/**
 * Admin – Benachrichtigungen (NT-ADMIN-NOTIFICATIONS-01)
 *
 * Zwei Bereiche:
 *   - Broadcast erstellen (sofort oder zeitversetzt geplant) + Übersicht/Stornieren
 *   - Scheine + Nachricht an einen einzelnen Spieler vergeben (Support/Kompensation)
 *
 * Routen (Backend):
 *   POST   /api/v1/admin/notifications/broadcast
 *   POST   /api/v1/admin/notifications/grant-scheine
 *   GET    /api/v1/admin/notifications
 *   DELETE /api/v1/admin/notifications/:id
 *   GET    /api/v1/admin/users?search=... (bestehender Endpunkt — Spielersuche)
 */

import { api, ApiError } from '../../services/api.js';
import { showToast } from '../../utils/toast.js';

type BroadcastType = 'UPDATE' | 'RESTART' | 'GENERAL' | 'SCHEINE_ANNOUNCEMENT';

interface AdminBroadcastItem {
  id: string;
  type: string;
  title: string;
  body: string;
  scheduledFor: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface AdminUserSearchResult {
  id: string;
  profile: { username: string } | null;
}

let containerId = '';
let selectedPlayer: { userId: string; username: string } | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

export function initAdminNotifications(targetContainerId: string): void {
  containerId = targetContainerId;
  selectedPlayer = null;
  renderSkeleton();
  void loadBroadcasts();
}

function renderSkeleton(): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  container.innerHTML = `
    <section style="margin-bottom:32px;">
      <h2 style="color:var(--accent-gold);font-size:15px;letter-spacing:0.08em;margin:0 0 12px;">
        BROADCAST ERSTELLEN
      </h2>
      <div style="display:flex;flex-direction:column;gap:10px;max-width:480px;">
        <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          TYP
          <select id="an-broadcast-type" style="${selectStyle()}">
            <option value="UPDATE">UPDATE</option>
            <option value="RESTART">NEUSTART</option>
            <option value="GENERAL">ALLGEMEIN</option>
            <option value="SCHEINE_ANNOUNCEMENT">SCHEINE-ANKÜNDIGUNG</option>
          </select>
        </label>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          TITEL
          <input id="an-broadcast-title" type="text" maxlength="200" style="${inputStyle()}" />
        </label>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          NACHRICHT
          <textarea id="an-broadcast-body" rows="3" maxlength="2000" style="${inputStyle()}resize:vertical;"></textarea>
        </label>
        <label style="display:flex;align-items:center;gap:8px;font-size:12px;color:var(--text-muted);">
          <input id="an-broadcast-scheduled" type="checkbox" />
          Zeitversetzt planen
        </label>
        <input id="an-broadcast-datetime" type="datetime-local" disabled style="${inputStyle()}opacity:0.5;" />
        <div>
          <button id="an-broadcast-submit" style="${primaryBtnStyle()}">BROADCAST ERSTELLEN</button>
        </div>
      </div>

      <h3 style="color:var(--text-muted);font-size:13px;letter-spacing:0.06em;margin:24px 0 8px;">
        GEPLANTE / VERSENDETE BROADCASTS
      </h3>
      <div id="an-broadcast-list">
        <p style="color:var(--text-muted);">LADE...</p>
      </div>
    </section>

    <section>
      <h2 style="color:var(--accent-gold);font-size:15px;letter-spacing:0.08em;margin:0 0 12px;">
        SCHEINE AN SPIELER VERGEBEN
      </h2>
      <div style="display:flex;flex-direction:column;gap:10px;max-width:480px;position:relative;">
        <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          SPIELER
          <input id="an-player-search" type="text" placeholder="Username suchen..." style="${inputStyle()}" autocomplete="off" />
        </label>
        <div id="an-player-results" style="display:none;position:absolute;top:56px;left:0;right:0;z-index:10;
             background:var(--bg-elevated);border:1px solid var(--border);max-height:200px;overflow-y:auto;"></div>
        <div id="an-player-selected" style="color:var(--accent-green);font-size:12px;display:none;"></div>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          BETRAG (SCHEINE)
          <input id="an-grant-amount" type="number" min="1" max="5000" step="1" style="${inputStyle()}" />
        </label>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--text-muted);">
          NACHRICHT
          <textarea id="an-grant-message" rows="2" maxlength="500" style="${inputStyle()}resize:vertical;"></textarea>
        </label>
        <div>
          <button id="an-grant-submit" style="${primaryBtnStyle()}">VERGEBEN</button>
        </div>
      </div>
    </section>
  `;

  attachHandlers();
}

function selectStyle(): string {
  return 'padding:8px 10px;background:var(--bg-elevated);border:1px solid var(--border);' +
    'color:var(--text-primary);font-family:\'Share Tech Mono\',monospace;font-size:13px;';
}

function inputStyle(): string {
  return 'padding:8px 10px;background:var(--bg-elevated);border:1px solid var(--border);' +
    'color:var(--text-primary);font-family:\'Share Tech Mono\',monospace;font-size:13px;outline:none;';
}

function primaryBtnStyle(): string {
  return 'border:1px solid var(--accent-green);color:var(--accent-green);background:transparent;' +
    'padding:8px 18px;cursor:pointer;font-family:\'Share Tech Mono\',monospace;font-size:13px;letter-spacing:0.05em;';
}

function attachHandlers(): void {
  const scheduledCheckbox = document.getElementById('an-broadcast-scheduled') as HTMLInputElement | null;
  const datetimeInput = document.getElementById('an-broadcast-datetime') as HTMLInputElement | null;
  scheduledCheckbox?.addEventListener('change', () => {
    if (!datetimeInput) return;
    datetimeInput.disabled = !scheduledCheckbox.checked;
    datetimeInput.style.opacity = scheduledCheckbox.checked ? '1' : '0.5';
  });

  document.getElementById('an-broadcast-submit')?.addEventListener('click', () => { void handleCreateBroadcast(); });
  document.getElementById('an-grant-submit')?.addEventListener('click', () => { void handleGrantScheine(); });

  const searchInput = document.getElementById('an-player-search') as HTMLInputElement | null;
  searchInput?.addEventListener('input', () => {
    selectedPlayer = null;
    hideSelectedPlayer();
    const query = searchInput.value.trim();
    if (searchDebounceTimer !== null) clearTimeout(searchDebounceTimer);
    if (query.length < 2) {
      renderPlayerResults([]);
      return;
    }
    searchDebounceTimer = setTimeout(() => { void searchPlayers(query); }, 350);
  });
}

// ─── Broadcasts ───────────────────────────────────────────────────────────────

async function loadBroadcasts(): Promise<void> {
  const listEl = document.getElementById('an-broadcast-list');
  if (!listEl) return;

  try {
    const broadcasts = await api.get<AdminBroadcastItem[]>('/admin/notifications');
    renderBroadcastList(broadcasts);
  } catch (err) {
    listEl.innerHTML = `<p style="color:var(--accent-red);">${escapeHtml(
      err instanceof ApiError ? err.message : 'Fehler beim Laden der Broadcasts.',
    )}</p>`;
  }
}

function renderBroadcastList(broadcasts: AdminBroadcastItem[]): void {
  const listEl = document.getElementById('an-broadcast-list');
  if (!listEl) return;

  if (broadcasts.length === 0) {
    listEl.innerHTML = `<p style="color:var(--text-muted);">KEINE BROADCASTS VORHANDEN.</p>`;
    return;
  }

  listEl.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:6px;">
      ${broadcasts.map((b) => broadcastRowHtml(b)).join('')}
    </div>
  `;

  listEl.querySelectorAll<HTMLButtonElement>('[data-an-cancel]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.anCancel;
      if (id) void handleCancelBroadcast(id);
    });
  });
}

function broadcastRowHtml(b: AdminBroadcastItem): string {
  const isScheduledPending = b.sentAt === null;
  const dateLabel = b.scheduledFor
    ? new Date(b.scheduledFor).toLocaleString('de-DE')
    : b.sentAt
      ? new Date(b.sentAt).toLocaleString('de-DE')
      : '–';
  const statusLabel = isScheduledPending ? '(geplant)' : '(versendet)';
  const cancelBtn = isScheduledPending
    ? `<button data-an-cancel="${b.id}" style="border:1px solid var(--accent-red);color:var(--accent-red);
         background:transparent;padding:3px 10px;cursor:pointer;font-family:'Share Tech Mono',monospace;font-size:11px;">
         STORNIEREN
       </button>`
    : '';

  return `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;
                padding:8px 10px;border:1px solid var(--border);font-size:12px;">
      <span style="color:var(--text-muted);white-space:nowrap;">${dateLabel} ${statusLabel}</span>
      <span style="color:var(--text-primary);flex:1;">${escapeHtml(b.title)}</span>
      <span style="color:var(--accent-blue);white-space:nowrap;">${escapeHtml(b.type)}</span>
      ${cancelBtn}
    </div>
  `;
}

async function handleCreateBroadcast(): Promise<void> {
  const typeEl = document.getElementById('an-broadcast-type') as HTMLSelectElement | null;
  const titleEl = document.getElementById('an-broadcast-title') as HTMLInputElement | null;
  const bodyEl = document.getElementById('an-broadcast-body') as HTMLTextAreaElement | null;
  const scheduledCheckbox = document.getElementById('an-broadcast-scheduled') as HTMLInputElement | null;
  const datetimeEl = document.getElementById('an-broadcast-datetime') as HTMLInputElement | null;
  if (!typeEl || !titleEl || !bodyEl || !scheduledCheckbox || !datetimeEl) return;

  const title = titleEl.value.trim();
  const body = bodyEl.value.trim();
  if (!title || !body) {
    showToast('Titel und Nachricht dürfen nicht leer sein.', 'error');
    return;
  }

  const payload: { type: BroadcastType; title: string; body: string; scheduledFor?: string } = {
    type: typeEl.value as BroadcastType,
    title,
    body,
  };
  if (scheduledCheckbox.checked && datetimeEl.value) {
    payload.scheduledFor = new Date(datetimeEl.value).toISOString();
  }

  try {
    await api.post('/admin/notifications/broadcast', payload);
    showToast('Broadcast erstellt.', 'info');
    titleEl.value = '';
    bodyEl.value = '';
    scheduledCheckbox.checked = false;
    datetimeEl.value = '';
    datetimeEl.disabled = true;
    datetimeEl.style.opacity = '0.5';
    void loadBroadcasts();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Erstellen des Broadcasts.', 'error');
  }
}

async function handleCancelBroadcast(id: string): Promise<void> {
  try {
    await api.delete(`/admin/notifications/${id}`);
    showToast('Broadcast storniert.', 'info');
    void loadBroadcasts();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Stornieren.', 'error');
  }
}

// ─── Scheine-Vergabe ──────────────────────────────────────────────────────────

async function searchPlayers(query: string): Promise<void> {
  try {
    // api.get() entpackt die {data, meta}-Hülle bereits automatisch (fetchApi::hasDataEnvelope) —
    // hier direkt das entpackte Array typisieren, nicht nochmal { data: [...] } erwarten.
    const users = await api.get<AdminUserSearchResult[]>(`/admin/users?search=${encodeURIComponent(query)}&limit=10`);
    renderPlayerResults(users);
  } catch {
    renderPlayerResults([]);
  }
}

function renderPlayerResults(users: AdminUserSearchResult[]): void {
  const resultsEl = document.getElementById('an-player-results');
  if (!resultsEl) return;

  const withProfile = users
    .map((u) => (u.profile ? { id: u.id, username: u.profile.username } : null))
    .filter((u): u is { id: string; username: string } => u !== null);

  if (withProfile.length === 0) {
    resultsEl.style.display = 'none';
    resultsEl.innerHTML = '';
    return;
  }

  resultsEl.style.display = 'block';
  resultsEl.innerHTML = withProfile.map((u) => `
    <button data-an-user-id="${u.id}" data-an-username="${escapeAttr(u.username)}"
            style="display:block;width:100%;text-align:left;padding:8px 10px;background:transparent;
                   border:none;border-bottom:1px solid var(--border);color:var(--text-primary);
                   cursor:pointer;font-family:'Share Tech Mono',monospace;font-size:13px;">
      ${escapeHtml(u.username)}
    </button>
  `).join('');

  resultsEl.querySelectorAll<HTMLButtonElement>('[data-an-user-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const userId = btn.dataset.anUserId;
      const username = btn.dataset.anUsername;
      if (!userId || username === undefined) return;
      selectedPlayer = { userId, username };
      showSelectedPlayer(username);
      resultsEl.style.display = 'none';
      resultsEl.innerHTML = '';
      const searchInput = document.getElementById('an-player-search') as HTMLInputElement | null;
      if (searchInput) searchInput.value = username;
    });
  });
}

function showSelectedPlayer(username: string): void {
  const el = document.getElementById('an-player-selected');
  if (!el) return;
  el.textContent = `AUSGEWÄHLT: ${username}`;
  el.style.display = 'block';
}

function hideSelectedPlayer(): void {
  const el = document.getElementById('an-player-selected');
  if (!el) return;
  el.style.display = 'none';
}

async function handleGrantScheine(): Promise<void> {
  const amountEl = document.getElementById('an-grant-amount') as HTMLInputElement | null;
  const messageEl = document.getElementById('an-grant-message') as HTMLTextAreaElement | null;
  if (!amountEl || !messageEl) return;

  if (!selectedPlayer) {
    showToast('Bitte zuerst einen Spieler auswählen.', 'error');
    return;
  }

  const amount = parseInt(amountEl.value, 10);
  const message = messageEl.value.trim();
  if (!Number.isInteger(amount) || amount <= 0) {
    showToast('Ungültiger Betrag.', 'error');
    return;
  }
  if (!message) {
    showToast('Nachricht darf nicht leer sein.', 'error');
    return;
  }

  try {
    await api.post('/admin/notifications/grant-scheine', {
      userId: selectedPlayer.userId,
      amount,
      message,
    });
    showToast(`${String(amount)} Scheine an ${selectedPlayer.username} vergeben.`, 'info');
    amountEl.value = '';
    messageEl.value = '';
    selectedPlayer = null;
    hideSelectedPlayer();
    const searchInput = document.getElementById('an-player-search') as HTMLInputElement | null;
    if (searchInput) searchInput.value = '';
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler bei der Scheine-Vergabe.', 'error');
  }
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;');
}
